import React, { useState, useEffect } from 'react';
import { matchingConfigurationAPI, MatchingConfiguration } from '../services/api';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Switch,
  FormControlLabel,
  Alert,
  Divider,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Settings,
  Save,
  Refresh,
  Info,
  TrendingUp,
  Warning,
  CheckCircle
} from '@mui/icons-material';



interface MatchingConfigurationPanelProps {
  config: MatchingConfiguration;
  onSave: (config: MatchingConfiguration) => void;
  onReset: () => void;
  isSaving?: boolean;
}

const MatchingConfigurationPanel: React.FC<MatchingConfigurationPanelProps> = ({
  config,
  onSave,
  onReset,
  isSaving = false
}) => {
  const [localConfig, setLocalConfig] = useState<MatchingConfiguration>(config);
  const [hasChanges, setHasChanges] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLocalConfig(config);
  }, [config]);

  useEffect(() => {
    setHasChanges(JSON.stringify(localConfig) !== JSON.stringify(config));
  }, [localConfig, config]);

  const handleChange = (field: keyof MatchingConfiguration, value: any) => {
    setLocalConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (localConfig.id) {
        await matchingConfigurationAPI.update(localConfig);
      } else {
        await matchingConfigurationAPI.create(localConfig);
      }
      
      onSave(localConfig);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const defaultConfig = await matchingConfigurationAPI.resetToDefaults();
      setLocalConfig(defaultConfig.data);
      onReset();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reset configuration');
    } finally {
      setLoading(false);
    }
  };

  const getToleranceDescription = (percentage: number) => {
    if (percentage <= 2) return 'Very Strict';
    if (percentage <= 5) return 'Strict';
    if (percentage <= 10) return 'Moderate';
    if (percentage <= 20) return 'Loose';
    return 'Very Loose';
  };

  const getDateToleranceDescription = (days: number) => {
    if (days <= 3) return 'Very Strict';
    if (days <= 7) return 'Strict';
    if (days <= 15) return 'Moderate';
    if (days <= 30) return 'Loose';
    return 'Very Loose';
  };

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box display="flex" alignItems="center" gap={1}>
            <Settings color="primary" />
            <Typography variant="h6">Matching Configuration</Typography>
          </Box>
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleReset}
              startIcon={<Refresh />}
              disabled={!hasChanges}
            >
              Reset
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleSave}
              startIcon={<Save />}
              disabled={!hasChanges || isSaving}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </Box>

        {hasChanges && (
          <Alert severity="info" sx={{ mb: 3 }}>
            You have unsaved changes. Click Save to apply the new configuration.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Amount Tolerance Settings */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" gutterBottom>
              Amount Matching
            </Typography>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Chip 
                label={getToleranceDescription(localConfig.amount_tolerance_percentage)}
                color={localConfig.amount_tolerance_percentage <= 5 ? 'success' : 'warning'}
                size="small"
              />
              <Tooltip title="How much the transaction amount can differ from the planned expense monthly amount">
                <IconButton size="small">
                  <Info />
                </IconButton>
              </Tooltip>
            </Box>
            
            <TextField
              fullWidth
              label="General Amount Tolerance (%)"
              type="number"
              value={localConfig.amount_tolerance_percentage}
              onChange={(e) => handleChange('amount_tolerance_percentage', parseFloat(e.target.value) || 0)}
              inputProps={{ min: 0, max: 100, step: 0.1 }}
              helperText="Maximum percentage difference for amount matching"
              sx={{ mb: 2 }}
            />
            
            <TextField
              fullWidth
              label="High Priority Amount Tolerance (%)"
              type="number"
              value={localConfig.high_priority_amount_tolerance}
              onChange={(e) => handleChange('high_priority_amount_tolerance', parseFloat(e.target.value) || 0)}
              inputProps={{ min: 0, max: 100, step: 0.1 }}
              helperText="Stricter tolerance for high-priority matches"
            />
          </Grid>

          {/* Date Tolerance Settings */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" gutterBottom>
              Date Matching
            </Typography>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Chip 
                label={getDateToleranceDescription(localConfig.due_date_tolerance_days)}
                color={localConfig.due_date_tolerance_days <= 7 ? 'success' : 'warning'}
                size="small"
              />
              <Tooltip title="How many days before/after the due date a transaction can be matched">
                <IconButton size="small">
                  <Info />
                </IconButton>
              </Tooltip>
            </Box>
            
            <TextField
              fullWidth
              label="General Date Tolerance (days)"
              type="number"
              value={localConfig.due_date_tolerance_days}
              onChange={(e) => handleChange('due_date_tolerance_days', parseInt(e.target.value) || 0)}
              inputProps={{ min: 0, max: 365, step: 1 }}
              helperText="Maximum days difference for date matching"
              sx={{ mb: 2 }}
            />
            
            <TextField
              fullWidth
              label="High Priority Date Tolerance (days)"
              type="number"
              value={localConfig.high_priority_date_tolerance}
              onChange={(e) => handleChange('high_priority_date_tolerance', parseInt(e.target.value) || 0)}
              inputProps={{ min: 0, max: 365, step: 1 }}
              helperText="Stricter tolerance for high-priority matches"
            />
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
          </Grid>

          {/* Scoring and Priority Settings */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" gutterBottom>
              Match Scoring
            </Typography>
            
            <TextField
              fullWidth
              label="Minimum Match Score"
              type="number"
              value={localConfig.minimum_match_score}
              onChange={(e) => handleChange('minimum_match_score', parseInt(e.target.value) || 0)}
              inputProps={{ min: 0, max: 100, step: 1 }}
              helperText="Minimum score required for automatic matching"
              sx={{ mb: 2 }}
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={localConfig.enable_frequency_bonus}
                  onChange={(e) => handleChange('enable_frequency_bonus', e.target.checked)}
                />
              }
              label="Enable Frequency Bonus"
            />
            <Typography variant="caption" display="block" color="textSecondary">
              Gives bonus points to monthly expenses for better matching
            </Typography>
            
            <FormControlLabel
              control={
                <Switch
                  checked={localConfig.enable_category_weighting}
                  onChange={(e) => handleChange('enable_category_weighting', e.target.checked)}
                />
              }
              label="Enable Category Weighting"
            />
            <Typography variant="caption" display="block" color="textSecondary">
              Prioritizes category matches over other criteria
            </Typography>
          </Grid>

          {/* Behavior Settings */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" gutterBottom>
              Matching Behavior
            </Typography>
            
            <FormControlLabel
              control={
                <Switch
                  checked={localConfig.auto_match_enabled}
                  onChange={(e) => handleChange('auto_match_enabled', e.target.checked)}
                />
              }
              label="Enable Automatic Matching"
            />
            <Typography variant="caption" display="block" color="textSecondary">
              Automatically match transactions when approved
            </Typography>
            
            <FormControlLabel
              control={
                <Switch
                  checked={localConfig.require_manual_review}
                  onChange={(e) => handleChange('require_manual_review', e.target.checked)}
                />
              }
              label="Require Manual Review"
            />
            <Typography variant="caption" display="block" color="textSecondary">
              Always show matches for manual confirmation
            </Typography>
          </Grid>
        </Grid>

        {/* Configuration Summary */}
        <Box mt={3} p={2} bgcolor="grey.50" borderRadius={1}>
          <Typography variant="subtitle2" gutterBottom>
            Configuration Summary
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={6} md={3}>
              <Chip 
                icon={<TrendingUp />}
                label={`${localConfig.amount_tolerance_percentage}% Amount`}
                size="small"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <Chip 
                icon={<CheckCircle />}
                label={`${localConfig.due_date_tolerance_days} Days`}
                size="small"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <Chip 
                icon={<Warning />}
                label={`Score: ${localConfig.minimum_match_score}+`}
                size="small"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <Chip 
                icon={localConfig.auto_match_enabled ? <CheckCircle /> : <Info />}
                label={localConfig.auto_match_enabled ? 'Auto-Enabled' : 'Manual Only'}
                size="small"
                variant="outlined"
                color={localConfig.auto_match_enabled ? 'success' : 'default'}
              />
            </Grid>
          </Grid>
        </Box>
      </CardContent>
    </Card>
  );
};

export default MatchingConfigurationPanel;
